import type { Bill, IncomeEvent } from "../api-modules/types.js";

/**
 * Cash-flow projection. Pure function — no IO. Web-side mirror of
 * `shared/src/util/cash-flow.ts` (5.1 lock — web doesn't import
 * @hearth/shared).
 *
 * Used by the dashboard `<CashFlowSparkline>` widget. See
 * `shared/src/util/cash-flow.ts` for the full design rationale.
 *
 * **Includes a local copy of `nextDueDate`** to keep the web import
 * surface narrow — the helper is small and re-exporting from a
 * separate file would just multiply the mirror count.
 */

export interface CashFlowMonth {
  readonly month: string;
  readonly inflow_cents: number;
  readonly outflow_cents: number;
  readonly net_cents: number;
  readonly cumulative_cents: number;
}

export interface CashFlowProjection {
  readonly months: readonly CashFlowMonth[];
  readonly total_inflow_cents: number;
  readonly total_outflow_cents: number;
  readonly net_cents: number;
  readonly ending_balance_cents: number;
}

export interface ProjectCashFlowInput {
  readonly bills: readonly Bill[];
  readonly income_events: readonly IncomeEvent[];
  readonly horizon_months: number;
  readonly today: string;
  readonly starting_balance_cents?: number;
  readonly income_lookback_months?: number;
}

export function projectCashFlow(
  input: ProjectCashFlowInput,
): CashFlowProjection {
  const {
    bills,
    income_events,
    horizon_months,
    today,
    starting_balance_cents = 0,
    income_lookback_months = 3,
  } = input;
  if (horizon_months < 1) {
    throw new Error(
      `projectCashFlow: horizon_months must be >= 1 (got ${horizon_months})`,
    );
  }

  const monthBuckets = enumerateMonths(today, horizon_months);
  const inflows = new Map<string, number>(monthBuckets.map((m) => [m, 0]));
  const outflows = new Map<string, number>(monthBuckets.map((m) => [m, 0]));

  const horizonEnd = lastDayOfMonth(monthBuckets[monthBuckets.length - 1]!);
  for (const bill of bills) {
    if (bill.is_archived === true) continue;
    const dueDates = upcomingBillDueDates(bill, today, horizonEnd);
    for (const due of dueDates) {
      const month = due.slice(0, 7);
      if (!outflows.has(month)) continue;
      outflows.set(month, (outflows.get(month) ?? 0) + bill.amount_cents);
    }
  }

  const monthlyIncomeAverageCents = averageMonthlyIncome(
    income_events,
    today,
    income_lookback_months,
  );
  for (const month of monthBuckets) {
    inflows.set(month, monthlyIncomeAverageCents);
  }

  const months: CashFlowMonth[] = [];
  let cumulative = starting_balance_cents;
  let totalIn = 0;
  let totalOut = 0;
  for (const month of monthBuckets) {
    const inflow = inflows.get(month) ?? 0;
    const outflow = outflows.get(month) ?? 0;
    const net = inflow - outflow;
    cumulative += net;
    totalIn += inflow;
    totalOut += outflow;
    months.push({
      month,
      inflow_cents: inflow,
      outflow_cents: outflow,
      net_cents: net,
      cumulative_cents: cumulative,
    });
  }

  return {
    months,
    total_inflow_cents: totalIn,
    total_outflow_cents: totalOut,
    net_cents: totalIn - totalOut,
    ending_balance_cents: cumulative,
  };
}

export function enumerateMonths(
  today: string,
  horizon_months: number,
): string[] {
  const [yStr, mStr] = today.split("-");
  const startY = Number(yStr);
  const startM = Number(mStr);
  const out: string[] = [];
  for (let i = 0; i < horizon_months; i++) {
    const total0 = (startY * 12 + (startM - 1)) + i;
    const y = Math.floor(total0 / 12);
    const m0 = total0 % 12;
    out.push(`${pad4(y)}-${pad2(m0 + 1)}`);
  }
  return out;
}

export function upcomingBillDueDates(
  bill: Bill,
  from: string,
  to: string,
): string[] {
  if (bill.is_archived === true) return [];
  const out: string[] = [];

  if (bill.recurrence === "one_time") {
    if (bill.due_date >= from && bill.due_date <= to) {
      out.push(bill.due_date);
    }
    return out;
  }

  let current = bill.due_date;
  let safety = 0;
  while (current < from && safety < 1000) {
    const next = nextDueDate(current, bill.recurrence, bill.start_date);
    if (next === null) return out;
    current = next;
    safety++;
  }
  while (current <= to && safety < 1000) {
    if (current >= from) out.push(current);
    const next = nextDueDate(current, bill.recurrence, bill.start_date);
    if (next === null) break;
    current = next;
    safety++;
  }
  return out;
}

export function averageMonthlyIncome(
  income_events: readonly IncomeEvent[],
  today: string,
  lookback_months: number,
): number {
  if (lookback_months < 1) return 0;
  const [yStr, mStr] = today.split("-");
  const todayY = Number(yStr);
  const todayM = Number(mStr);

  const endTotal0 = todayY * 12 + (todayM - 1) - 1;
  const startTotal0 = endTotal0 - (lookback_months - 1);
  if (endTotal0 < startTotal0) return 0;

  let total = 0;
  for (const evt of income_events) {
    if (evt.is_archived === true) continue;
    const eventMonth = evt.date.slice(0, 7);
    const [eyStr, emStr] = eventMonth.split("-");
    const eyN = Number(eyStr);
    const emN = Number(emStr);
    const evtTotal0 = eyN * 12 + (emN - 1);
    if (evtTotal0 < startTotal0 || evtTotal0 > endTotal0) continue;
    total += evt.amount_cents;
  }
  return Math.round(total / lookback_months);
}

// === local nextDueDate (mirror of shared/src/util/bills.ts) ============

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function recurrenceIntervalMonths(
  recurrence: Bill["recurrence"],
): number | null {
  switch (recurrence) {
    case "monthly":
      return 1;
    case "quarterly":
      return 3;
    case "annual":
      return 12;
    case "one_time":
      return null;
  }
}

function nextDueDate(
  currentDue: string,
  recurrence: Bill["recurrence"],
  startDate: string,
): string | null {
  if (!ISO_DATE_RE.test(currentDue) || !ISO_DATE_RE.test(startDate)) {
    return null;
  }
  const interval = recurrenceIntervalMonths(recurrence);
  if (interval === null) return null;
  const [curY, curM] = currentDue.split("-").map(Number) as [
    number,
    number,
    number,
  ];
  const [, , anchorD] = startDate.split("-").map(Number) as [
    number,
    number,
    number,
  ];
  const total0 = (curY * 12 + (curM - 1)) + interval;
  const newY = Math.floor(total0 / 12);
  const newM0 = total0 % 12;
  const lastDay = new Date(Date.UTC(newY, newM0 + 1, 0)).getUTCDate();
  const newD = Math.min(anchorD, lastDay);
  return `${pad4(newY)}-${pad2(newM0 + 1)}-${pad2(newD)}`;
}

function lastDayOfMonth(yyyymm: string): string {
  const [yStr, mStr] = yyyymm.split("-");
  const y = Number(yStr);
  const m0 = Number(mStr) - 1;
  const lastDay = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  return `${pad4(y)}-${pad2(m0 + 1)}-${pad2(lastDay)}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
function pad4(n: number): string {
  return n.toString().padStart(4, "0");
}
